// ============================================================
// Dashboard data context.
//
// Three lifecycle states the provider can be in:
//   - 'loading'       fetching session + initial data
//   - 'unauthed'      no session / 401 — render <LoginPage>
//   - 'ready'         data loaded, render dashboard
//   - 'error'         hard error (network down, etc.)
//
// When VITE_RISMA_API_URL is set, we go through the live flow:
//   /v1/me → if 401, show LoginPage; if 200, load the rest.
// When it isn't, we skip auth entirely and use the bundled mock fixtures
// (offline template-preview mode).
// ============================================================
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { dateLabelFor, todayRome } from '../lib/dates';
import {
  createClient,
  ApiError,
  type RismaClient,
  type TenantFeatures,
  type FeatureKey,
  type ReservationCreateInput,
  type ReservationPatchInput,
  type CustomerPatchInput,
  type SettingsPatchInput,
  type ZoneCreateInput,
  type ZonePatchInput,
  type TableCreateInput,
  type TablePatchInput,
  type WireTenantSettings,
  type WireSiteContent,
  type SiteContent,
  type SiteLocaleOverlay,
} from '@risma/data-client';
import type {
  Customer,
  CustomerTag,
  DateLabel,
  DayHours,
  Insights,
  NotificationItem,
  Reservation,
  ReservationStatus,
  Service,
  ServiceCfg,
  Table,
  Tenant,
  Zone,
} from './types';

/**
 * Default features for mock/offline mode — every core feature on, no
 * planned features. Matches what the API returns by default; kept in
 * sync with apps/api/src/db/features.ts.
 */
const MOCK_FEATURES: TenantFeatures = {
  reservations: true,
  customers: true,
  notifications: true,
  insights: true,
  availability: true,
  settings: true,
  website: true,
  autoConfirm: true,
  emailNotif: true,
  waitlist: false,
  onlineOrdering: false,
  promotions: false,
  newsletter: false,
  events: false,
  googleReviews: false,
};
import {
  customers as mockCustomers,
  dateLabel as mockDateLabel,
  hours as mockHours,
  insights as mockInsights,
  notifications as mockNotifications,
  reservations as mockReservations,
  services as mockServices,
  serviceCfg as mockServiceCfg,
  tables as mockTables,
  tenant as mockTenant,
  zones as mockZones,
} from './mock';
import { LoginPage } from '../auth/LoginPage';

/** Everything the dashboard renders against — same shape regardless of source. */
export interface DashboardData {
  services: Service[];
  zones: Zone[];
  tables: Table[];
  reservations: Reservation[];
  customers: Customer[];
  hours: DayHours[];
  serviceCfg: ServiceCfg;
  insights: Insights;
  notifications: NotificationItem[];
  tenant: Tenant;
  dateLabel: DateLabel;
  /** Giorno selezionato (YYYY-MM-DD) — il calendario mostra solo questo giorno. */
  day: string;
  /** Resolved feature flags — every known feature key has a value. */
  features: TenantFeatures;
  /** Owner-controlled settings (auto-confirm, email notif, etc.). */
  settings: WireTenantSettings;
  /** True when an operator/partner is impersonating this owner (staff access). */
  isImpersonated: boolean;
  impersonatedBy: { email: string; name: string | null } | null;
}

const MOCK_SETTINGS: WireTenantSettings = {
  autoConfirm: true,
  autoConfirmThreshold: 6,
  maxConcurrentCovers: 50,
  guestBooking: true,
  emailNotif: true,
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'unauthed' }
  | { kind: 'ready'; data: DashboardData; source: 'api' | 'mock' }
  | { kind: 'error'; error: Error };

/** Mutation actions exposed to views via useDashboardActions(). */
export interface DashboardActions {
  createReservation: (input: ReservationCreateInput) => Promise<Reservation>;
  setReservationStatus: (id: string, status: ReservationStatus) => Promise<void>;
  /** Full reservation patch — name, time, covers, table, notes, etc. */
  patchReservation: (id: string, patch: ReservationPatchInput) => Promise<Reservation>;
  setCustomerTags: (id: string, tags: CustomerTag[]) => Promise<void>;
  /** Full customer patch — name, phone, email, fav, notes. */
  patchCustomer: (id: string, patch: CustomerPatchInput) => Promise<Customer>;
  markAllNotificationsRead: () => Promise<void>;
  markNotificationRead: (id: string, read: boolean) => Promise<void>;
  /** Update owner-controlled tenant settings (auto-confirm etc.). */
  updateSettings: (patch: SettingsPatchInput) => Promise<void>;
  // Sala — zone + tavoli (#39)
  createZone: (input: ZoneCreateInput) => Promise<void>;
  updateZone: (dbId: string, patch: ZonePatchInput) => Promise<void>;
  deleteZone: (dbId: string) => Promise<void>;
  createTable: (input: TableCreateInput) => Promise<void>;
  updateTable: (dbId: string, patch: TablePatchInput) => Promise<void>;
  deleteTable: (dbId: string) => Promise<void>;
  // ── website editor ──────────────────────────────────────────
  /** Load the tenant's website content (draft + published). */
  loadSite: () => Promise<WireSiteContent>;
  /** Save the working draft. */
  saveSiteDraft: (content: SiteContent) => Promise<void>;
  /** Save ONLY the brand colours — non bloccato dalla validazione del resto. */
  saveTheme: (theme: NonNullable<SiteContent['theme']>) => Promise<void>;
  /** Publish the current draft. */
  publishSite: () => Promise<void>;
  /** Upload a photo; resolves to its public URL. */
  uploadImage: (file: Blob) => Promise<string>;
  /** Genera la traduzione inglese del contenuto (Claude) e la salva nel draft;
   *  torna l'overlay generato. Lancia se il server non ha la chiave / fallisce. */
  translateSite: () => Promise<SiteLocaleOverlay>;
}

interface DashboardCtx {
  data: DashboardData;
  actions: DashboardActions;
  /** Calls /v1/auth/logout and reloads. Only present when source === 'api'. */
  logout: (() => Promise<void>) | null;
  /** Giorno selezionato del calendario (YYYY-MM-DD) — guidato dal date
   *  stepper. Accetta anche l'updater funzionale di React, così i click
   *  rapidi sul ‹/› non si perdono (niente closure stantia). */
  setDay: (day: string | ((prev: string) => string)) => void;
}

const Ctx = createContext<DashboardCtx | null>(null);

const apiUrl = import.meta.env.VITE_RISMA_API_URL?.trim();

/**
 * Lo slug del tenant a cui appartiene questo portale: il robot lo inietta a
 * build time (VITE_RISMA_TENANT_SLUG = slug del cliente), così il login chiede
 * il magic-link al tenant GIUSTO. In dev (nessuna env) ripiega su 'carlotta',
 * il tenant del seed locale.
 */
const TENANT_SLUG_FOR_LOGIN = import.meta.env.VITE_RISMA_TENANT_SLUG?.trim() || 'carlotta';

// ── wire → internal mappers ──────────────────────────────────
// These are reused by both the initial load and mutation responses.
function reservationFromWire(r: import('@risma/data-client').WireReservation): Reservation {
  return {
    id: r.id,
    name: r.name,
    covers: r.covers,
    table: r.table ?? '',
    start: r.start,
    startsAt: r.startsAt,
    dur: r.durationMinutes,
    status: r.status as Reservation['status'],
    source: r.source as Reservation['source'],
    phone: r.phone ?? '',
    tags: r.tags as Reservation['tags'],
    note: r.note,
    cust: r.customerId,
    service: r.service as Reservation['service'],
  };
}

function customerFromWire(c: import('@risma/data-client').WireCustomer): Customer {
  return {
    id: c.id,
    name: c.name,
    visits: c.visits,
    last: c.lastVisit ?? '',
    spend:
      c.averageSpendCents != null ? `€${Math.round(c.averageSpendCents / 100)}` : '—',
    tags: c.tags as Customer['tags'],
    phone: c.phone ?? '',
    email: c.email ?? '',
    note: c.note,
    fav: c.favouriteDish ?? '—',
  };
}

/** "2026-06-03T03:51:33Z" → "4 min fa" / "4 min ago" (static, computed at load). */
function relTime(iso: string): { it: string; en: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { it: iso, en: iso };
  const mins = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (mins < 1) return { it: 'adesso', en: 'just now' };
  if (mins < 60) return { it: `${mins} min fa`, en: `${mins} min ago` };
  const hours = Math.floor(mins / 60);
  if (hours < 24)
    return {
      it: hours === 1 ? '1 ora fa' : `${hours} ore fa`,
      en: hours === 1 ? '1 hour ago' : `${hours} hours ago`,
    };
  const days = Math.floor(hours / 24);
  if (days === 1) return { it: 'ieri', en: 'yesterday' };
  if (days < 7) return { it: `${days} giorni fa`, en: `${days} days ago` };
  return {
    it: d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }),
    en: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
  };
}

async function loadFromApi(baseUrl: string): Promise<DashboardData> {
  const client = createClient({ baseUrl });

  const [me, reservations, customers, zonesPayload, notifications] = await Promise.all([
    client.me(),
    client.reservations(),
    client.customers(),
    client.zones(),
    client.notifications(),
  ]);

  const reservationsOut: Reservation[] = reservations.data.map(reservationFromWire);
  const customersOut: Customer[] = customers.data.map(customerFromWire);

  const zonesOut: Zone[] = zonesPayload.data.zones.map((z) => ({
    id: z.slug,
    name: z.name,
    sortOrder: z.sortOrder,
    bookableOnline: z.bookableOnline,
    dbId: z.id,
  }));
  const tablesOut: Table[] = zonesPayload.data.tables.map((t) => ({
    id: t.label,
    zone: t.zone,
    seats: t.seats,
    dbId: t.id,
  }));

  const notificationsOut: NotificationItem[] = notifications.data.map((n) => ({
    id: n.id,
    type: n.type as NotificationItem['type'],
    read: n.read,
    text: n.text,
    time: relTime(n.createdAt),
    reservationId: n.reservationId,
    actionable: n.actionable,
  }));

  const tenantOut: Tenant = {
    name: me.data.tenant.name,
    sub: me.data.tenant.sub ?? '',
    mono: me.data.tenant.mono,
    owner: me.data.user.name ?? me.data.user.email,
    ownerRole: { it: 'Titolare', en: 'Owner' },
    // White-label colours from the tenant row (accent set in the Console);
    // sidebar tint is derived in App.tsx when not provided.
    brand: me.data.tenant.brand
      ? {
          accent: me.data.tenant.brand.accent,
          sidebar: me.data.tenant.brand.sidebar ?? undefined,
        }
      : undefined,
    designStyle: me.data.tenant.designStyle,
    heroLayout: me.data.tenant.heroLayout ?? null,
    sectionNumerals: me.data.tenant.sectionNumerals ?? null,
    template: me.data.tenant.template,
    templateConfig: me.data.tenant.templateConfig,
  };

  return {
    reservations: reservationsOut,
    customers: customersOut,
    zones: zonesOut,
    tables: tablesOut,
    notifications: notificationsOut,
    tenant: tenantOut,
    features: me.data.features,
    settings: me.data.settings,
    isImpersonated: me.data.isImpersonated ?? false,
    impersonatedBy: me.data.impersonatedBy ?? null,
    services: mockServices,
    hours: mockHours,
    serviceCfg: mockServiceCfg,
    insights: mockInsights,
    // Placeholder: il provider li sovrascrive col giorno selezionato.
    dateLabel: mockDateLabel,
    day: '2026-06-12',
  };
}

function loadFromMock(): DashboardData {
  return {
    services: mockServices,
    zones: mockZones,
    tables: mockTables,
    reservations: mockReservations,
    customers: mockCustomers,
    hours: mockHours,
    serviceCfg: mockServiceCfg,
    insights: mockInsights,
    notifications: mockNotifications,
    tenant: mockTenant,
    // Placeholder: il provider li sovrascrive col giorno selezionato.
    dateLabel: mockDateLabel,
    day: '2026-06-12',
    features: MOCK_FEATURES,
    settings: MOCK_SETTINGS,
    isImpersonated: false,
    impersonatedBy: null,
  };
}

export function DashboardProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LoadState>(() =>
    apiUrl ? { kind: 'loading' } : { kind: 'ready', data: loadFromMock(), source: 'mock' },
  );

  useEffect(() => {
    if (!apiUrl) return;
    let cancelled = false;

    // Vero quando questa pagina ha appena consumato un token di impersonazione.
    // Serve al ripiego mock qui sotto: loadFromMock() ha isImpersonated:false di
    // default, ma se l'API singhiozza SUBITO dopo l'hand-off staff dobbiamo
    // comunque sapere che è impersonato, altrimenti l'editor riattiverebbe il
    // prefill dall'anteprima ospite (contenuti di un altro tenant).
    let impersonating = false;

    (async () => {
      // Staff impersonation hand-off: the Console opens /__staff?token=… —
      // consume the token to mint an (impersonated) owner session, then clean
      // the URL and load the dashboard as that owner.
      try {
        const params = new URLSearchParams(window.location.search);
        const token = window.location.pathname === '/__staff' ? params.get('token') : null;
        if (token) {
          try {
            await createClient({ baseUrl: apiUrl }).consumeImpersonation(token);
            impersonating = true;
          } catch (e) {
            console.warn('[dashboard] impersonation consume failed:', e);
          }
          const view = params.get('view');
          window.history.replaceState({}, '', view ? `/?view=${view}` : '/');
        }
      } catch {
        /* ignore URL parsing issues */
      }

      try {
        const data = await loadFromApi(apiUrl);
        if (!cancelled) setState({ kind: 'ready', data, source: 'api' });
      } catch (err: unknown) {
        if (cancelled) return;
        // 401 → not logged in. Show the login page.
        if (err instanceof ApiError && err.status === 401) {
          setState({ kind: 'unauthed' });
          return;
        }
        // Network failure → assume API isn't running and fall back to
        // mock so the template stays usable in offline dev.
        if (!(err instanceof ApiError)) {
          console.warn('[dashboard] API unreachable, falling back to mock:', err);
          setState({ kind: 'ready', data: { ...loadFromMock(), isImpersonated: impersonating }, source: 'mock' });
          return;
        }
        // Any other API error (500, etc.) is a real error worth showing.
        setState({ kind: 'error', error: err });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // The actions hook is built outside the conditional renders below so it
  // has stable identity across renders. We can't useMemo inside a branch.
  const ready = state.kind === 'ready' ? state : null;
  const client: RismaClient | null =
    ready?.source === 'api' && apiUrl ? createClient({ baseUrl: apiUrl }) : null;

  const actions = useMemo<DashboardActions>(() => buildActions(client, setState), [client]);

  // Giorno selezionato del calendario. Coi dati veri si parte da oggi
  // (Europe/Rome — i ristoranti sono in Italia); in mock dal giorno
  // delle fixture, così la demo resta popolata.
  const [day, setDay] = useState<string>(() => (apiUrl ? todayRome() : '2026-06-12'));

  if (state.kind === 'loading') return <LoadingScreen />;
  if (state.kind === 'unauthed') {
    return <LoginPage apiUrl={apiUrl!} tenantSlug={TENANT_SLUG_FOR_LOGIN} />;
  }
  if (state.kind === 'error') return <ErrorScreen error={state.error} />;

  const logout =
    state.source === 'api' && apiUrl
      ? async () => {
          try {
            const c = createClient({ baseUrl: apiUrl });
            await c.logout();
          } catch (err) {
            console.warn('[dashboard] logout failed (continuing to reload):', err);
          }
          window.location.reload();
        }
      : null;

  return (
    <Ctx.Provider
      value={{
        data: { ...state.data, day, dateLabel: dateLabelFor(day) },
        actions,
        logout,
        setDay,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

/** Giorno selezionato + setter — per il date stepper della topbar. */
export function useDay(): {
  day: string;
  setDay: (day: string | ((prev: string) => string)) => void;
} {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDay() must be used inside <DashboardProvider>');
  return { day: v.data.day, setDay: v.setDay };
}

export function useDashboard(): DashboardData {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error('useDashboard() must be used inside <DashboardProvider>');
  }
  return v.data;
}

/** Shorthand: read a single feature flag. */
export function useFeature(key: FeatureKey): boolean {
  return useDashboard().features[key];
}

/** Mutation actions — same surface in API mode and mock mode. */
export function useDashboardActions(): DashboardActions {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error('useDashboardActions() must be used inside <DashboardProvider>');
  }
  return v.actions;
}

// ── mutation helpers ─────────────────────────────────────────
function buildActions(
  client: RismaClient | null,
  setState: React.Dispatch<React.SetStateAction<LoadState>>,
): DashboardActions {
  // Helper: produce a new state where `mutate` has been applied to the
  // existing dashboard data. No-ops if state isn't 'ready'.
  const mutateData = (
    mutate: (d: DashboardData) => DashboardData,
  ): void => {
    setState((prev) =>
      prev.kind === 'ready' ? { ...prev, data: mutate(prev.data) } : prev,
    );
  };

  // Dopo una modifica a zone/tavoli ricarico dall'API (verità del server: slug
  // e label si rigenerano lì) e aggiorno lo stato locale.
  const refreshZones = async (): Promise<void> => {
    if (!client) return;
    const p = await client.zones();
    mutateData((d) => ({
      ...d,
      zones: p.data.zones.map((z) => ({ id: z.slug, name: z.name, sortOrder: z.sortOrder, bookableOnline: z.bookableOnline, dbId: z.id })),
      tables: p.data.tables.map((t) => ({ id: t.label, zone: t.zone, seats: t.seats, dbId: t.id })),
    }));
  };

  return {
    async createReservation(input) {
      let created: Reservation;
      if (client) {
        const res = await client.createReservation(input);
        created = reservationFromWire(res.data);
      } else {
        // Mock mode: build a local reservation with a synthetic id.
        // The shape matches what the API would have returned.
        created = {
          id: localId(),
          name: input.name,
          covers: input.covers,
          // tableId is a UUID from the API; in mock land we want the
          // table label (e.g. "T3"). Callers can pass either via the
          // `tableId` field — if it doesn't look like a UUID we treat
          // it as a label.
          table: input.tableId ?? '',
          start: input.startsAt.slice(11, 16), // "HH:mm"
          dur: input.durationMinutes ?? 105,
          status: (input.status ?? 'new') as Reservation['status'],
          source: input.source,
          phone: input.phone ?? '',
          tags: (input.tags ?? []) as Reservation['tags'],
          note: { it: input.noteIt ?? '', en: input.noteEn ?? '' },
          cust: input.customerId ?? null,
          service: input.service as Reservation['service'],
        };
      }
      mutateData((d) => ({ ...d, reservations: [...d.reservations, created] }));
      return created;
    },

    async setReservationStatus(id, status) {
      if (client) {
        const res = await client.updateReservation(id, { status });
        const updated = reservationFromWire(res.data);
        mutateData((d) => ({
          ...d,
          reservations: d.reservations.map((r) => (r.id === id ? updated : r)),
        }));
      } else {
        mutateData((d) => ({
          ...d,
          reservations: d.reservations.map((r) =>
            r.id === id ? { ...r, status } : r,
          ),
        }));
      }
    },

    async patchReservation(id, patch) {
      let updated: Reservation;
      if (client) {
        const res = await client.updateReservation(id, patch);
        updated = reservationFromWire(res.data);
      } else {
        // Mock mode: apply the patch fields directly to the local row.
        // Field shapes don't always match 1:1 (e.g. patch.startsAt is
        // ISO, our internal type stores start as "HH:mm") — translate
        // the few that differ.
        let merged: Partial<Reservation> = {};
        if (patch.status !== undefined) merged.status = patch.status;
        if (patch.name !== undefined) merged.name = patch.name;
        if (patch.phone !== undefined) merged.phone = patch.phone ?? '';
        if (patch.covers !== undefined) merged.covers = patch.covers;
        if (patch.startsAt !== undefined) merged.start = patch.startsAt.slice(11, 16);
        if (patch.service !== undefined) merged.service = patch.service as Reservation['service'];
        if (patch.tableId !== undefined) merged.table = patch.tableId ?? '';
        if (patch.durationMinutes !== undefined) merged.dur = patch.durationMinutes;
        if (patch.tags !== undefined) merged.tags = patch.tags as Reservation['tags'];
        if (patch.noteIt !== undefined || patch.noteEn !== undefined) {
          // Coerce to local Localized shape, preserving existing values when not provided.
          // We don't have the prev row in scope here — fold via state update.
        }
        let result!: Reservation;
        mutateData((d) => {
          const next = d.reservations.map((r) => {
            if (r.id !== id) return r;
            const note = {
              it: patch.noteIt !== undefined ? (patch.noteIt ?? '') : r.note.it,
              en: patch.noteEn !== undefined ? (patch.noteEn ?? '') : r.note.en,
            };
            const u: Reservation = { ...r, ...merged, note };
            result = u;
            return u;
          });
          return { ...d, reservations: next };
        });
        updated = result;
        return updated;
      }
      mutateData((d) => ({
        ...d,
        reservations: d.reservations.map((r) => (r.id === id ? updated : r)),
      }));
      return updated;
    },

    async setCustomerTags(id, tags) {
      if (client) {
        const res = await client.updateCustomer(id, { tags });
        const updated = customerFromWire(res.data);
        mutateData((d) => ({
          ...d,
          customers: d.customers.map((c) => (c.id === id ? updated : c)),
        }));
      } else {
        mutateData((d) => ({
          ...d,
          customers: d.customers.map((c) => (c.id === id ? { ...c, tags } : c)),
        }));
      }
    },

    async patchCustomer(id, patch) {
      let updated: Customer;
      if (client) {
        const res = await client.updateCustomer(id, patch);
        updated = customerFromWire(res.data);
        mutateData((d) => ({
          ...d,
          customers: d.customers.map((c) => (c.id === id ? updated : c)),
        }));
      } else {
        let result!: Customer;
        mutateData((d) => {
          const next = d.customers.map((c) => {
            if (c.id !== id) return c;
            const note = {
              it: patch.noteIt !== undefined ? (patch.noteIt ?? '') : c.note.it,
              en: patch.noteEn !== undefined ? (patch.noteEn ?? '') : c.note.en,
            };
            const u: Customer = {
              ...c,
              ...(patch.name !== undefined ? { name: patch.name } : {}),
              ...(patch.phone !== undefined ? { phone: patch.phone ?? '' } : {}),
              ...(patch.email !== undefined ? { email: patch.email ?? '' } : {}),
              ...(patch.favouriteDish !== undefined ? { fav: patch.favouriteDish ?? '—' } : {}),
              ...(patch.tags !== undefined ? { tags: patch.tags as Customer['tags'] } : {}),
              note,
            };
            result = u;
            return u;
          });
          return { ...d, customers: next };
        });
        updated = result;
      }
      return updated;
    },

    async markAllNotificationsRead() {
      if (client) {
        await client.markAllNotificationsRead();
      }
      mutateData((d) => ({
        ...d,
        notifications: d.notifications.map((n) => ({ ...n, read: true })),
      }));
    },

    async markNotificationRead(id, read) {
      if (client) {
        await client.updateNotification(id, { read });
      }
      mutateData((d) => ({
        ...d,
        notifications: d.notifications.map((n) => (n.id === id ? { ...n, read } : n)),
      }));
    },

    async updateSettings(patch) {
      if (client) {
        const res = await client.updateSettings(patch);
        // Server returns the full WireMe with resolved settings; mirror
        // the resolved values into our local DashboardData.
        mutateData((d) => ({ ...d, settings: res.data.settings }));
      } else {
        mutateData((d) => ({
          ...d,
          settings: { ...d.settings, ...patch },
        }));
      }
    },

    // ── sala: zone + tavoli (#39) ───────────────────────────
    async createZone(input) {
      if (client) { await client.createZone(input); await refreshZones(); return; }
      const id = localId();
      mutateData((d) => ({ ...d, zones: [...d.zones, { id, name: { it: input.nameIt, en: input.nameEn ?? input.nameIt }, bookableOnline: input.bookableOnline ?? true, dbId: id }] }));
    },
    async updateZone(dbId, patch) {
      if (client) { await client.updateZone(dbId, patch); await refreshZones(); return; }
      mutateData((d) => ({ ...d, zones: d.zones.map((z) => (z.dbId === dbId ? { ...z, name: patch.nameIt !== undefined ? { it: patch.nameIt, en: patch.nameEn ?? z.name.en } : z.name, bookableOnline: patch.bookableOnline ?? z.bookableOnline } : z)) }));
    },
    async deleteZone(dbId) {
      if (client) { await client.deleteZone(dbId); await refreshZones(); return; }
      mutateData((d) => { const z = d.zones.find((x) => x.dbId === dbId); return { ...d, zones: d.zones.filter((x) => x.dbId !== dbId), tables: z ? d.tables.filter((t) => t.zone !== z.id) : d.tables }; });
    },
    async createTable(input) {
      if (client) { await client.createTable(input); await refreshZones(); return; }
      mutateData((d) => { const z = d.zones.find((x) => x.dbId === input.zoneId); return { ...d, tables: [...d.tables, { id: input.label, zone: z?.id ?? '', seats: input.seats, dbId: localId() }] }; });
    },
    async updateTable(dbId, patch) {
      if (client) { await client.updateTable(dbId, patch); await refreshZones(); return; }
      mutateData((d) => ({ ...d, tables: d.tables.map((t) => (t.dbId === dbId ? { ...t, id: patch.label ?? t.id, seats: patch.seats ?? t.seats } : t)) }));
    },
    async deleteTable(dbId) {
      if (client) { await client.deleteTable(dbId); await refreshZones(); return; }
      mutateData((d) => ({ ...d, tables: d.tables.filter((t) => t.dbId !== dbId) }));
    },

    // ── website editor ──────────────────────────────────────
    async loadSite() {
      if (client) return (await client.getSite()).data;
      return mockSite;
    },
    async saveSiteDraft(content) {
      if (client) {
        await client.saveSiteDraft(content);
      } else {
        mockSite = { ...mockSite, draft: content, updatedAt: new Date().toISOString() };
      }
    },
    async saveTheme(theme) {
      if (client) {
        await client.saveSiteTheme(theme);
      } else {
        mockSite = {
          ...mockSite,
          draft: { ...mockSite.draft, theme },
          published: mockSite.published ? { ...mockSite.published, theme } : mockSite.published,
          updatedAt: new Date().toISOString(),
        };
      }
    },
    async publishSite() {
      if (client) {
        await client.publishSite();
      } else {
        mockSite = { ...mockSite, published: mockSite.draft, publishedAt: new Date().toISOString() };
      }
    },
    async uploadImage(file) {
      if (client) return (await client.uploadSiteImage(file)).data.url;
      // Mock mode (no API): a local object URL keeps the editor usable offline.
      return URL.createObjectURL(file);
    },
    async translateSite() {
      if (client) return (await client.translateSite()).data.overlay;
      // Offline/mock: nessuna traduzione disponibile.
      throw new Error('translation_unavailable');
    },
  };
}

// Mock-mode website content store (no API). Lets the editor work offline.
let mockSite: WireSiteContent = { draft: {}, published: null, updatedAt: null, publishedAt: null };

/** Best-effort local id for mock-mode inserts. Real ids come from the API. */
function localId(): string {
  return `local-${Math.random().toString(36).slice(2, 10)}`;
}

/** Logout handler — null when running against mock data (no session to revoke). */
export function useLogout(): (() => Promise<void>) | null {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error('useLogout() must be used inside <DashboardProvider>');
  }
  return v.logout;
}

// ── loading / error states ──────────────────────────────────
function LoadingScreen() {
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        height: '100vh',
        background: 'var(--bg)',
        color: 'var(--mute)',
        fontFamily: 'var(--body)',
        fontSize: 14,
        letterSpacing: '0.05em',
      }}
    >
      <span>Caricamento…</span>
    </div>
  );
}

function ErrorScreen({ error }: { error: Error }) {
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        height: '100vh',
        background: 'var(--bg)',
        color: 'var(--st-noshow)',
        padding: 24,
        fontFamily: 'var(--body)',
        fontSize: 14,
      }}
    >
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        <div style={{ fontFamily: 'var(--display)', fontSize: 22, color: 'var(--ink)' }}>
          Errore di caricamento
        </div>
        <pre style={{ marginTop: 12, color: 'var(--mute)', fontSize: 12 }}>
          {error.message}
        </pre>
      </div>
    </div>
  );
}
